import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import { Stack, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { type RiskLiquidation } from '../../models/risk/risk-liquidation'
import { isValidNumberString } from '../../utils/string-utils'
import StackColumnLayout from '../../layouts/stack-column-layout'
import ScrollableList from '../../components/list/scrollable-list'
import RiskCard from '../../components/card/risk/risk-card'
import RiskSummary from '../../components/risk/RiskSummary'

interface RiskCardPanelProps {
  side: 'left' | 'right'
  showRatio: boolean
  filterSymbol: string
  setFilterSymbol: (value: string) => void
  filterAccount: string
  setFilterAccount: (value: string) => void
  riskItems: RiskLiquidation[]
  optionsSymbol: string[]
  optionsAccount: string[]
}

const RiskCardPanel: React.FC<RiskCardPanelProps> = ({
  side,
  showRatio,
  filterSymbol,
  setFilterSymbol,
  filterAccount,
  setFilterAccount,
  riskItems,
  optionsSymbol,
  optionsAccount
}) => {
  const [filteredOptionsSymbol, setFilteredOptionsSymbol] = useState<string[]>(optionsSymbol)
  const [filteredOptionsAccount, setFilteredOptionsAccount] = useState<string[]>(optionsAccount)

  useEffect(() => {
    setFilteredOptionsSymbol(optionsSymbol)
    setFilteredOptionsAccount(optionsAccount)
  }, [optionsSymbol, optionsAccount])
  return (
    <StackColumnLayout>
      <Autocomplete
        id={`filter-symbol-${side}`}
        options={filteredOptionsSymbol}
        value={filterSymbol}
        onChange={(event, value) => { setFilterSymbol(value ?? '') }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label={'Symbol'}
            size="small" fullWidth
          />
        )}
        sx={{ mb: 1 }}
      />

      <Autocomplete
        id={`filter-account-${side}`}
        options={filteredOptionsAccount}
        value={filterAccount}
        onChange={(event, value) => { setFilterAccount(value ?? '') }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label={'Account'}
            size="small"
            fullWidth
          />
        )}
        sx={{ mb: 1 }}
      />

      <ScrollableList slotCount={0}>
        {riskItems.map((data) => (
          <RiskCard
            key={`${side}-${data.id}`}
            {...data}
            show_ratio={showRatio}
          />
        ))}
      </ScrollableList>
    </StackColumnLayout>
  )
}

const RiskCardList: React.FunctionComponent = () => {
  const { data, error } = useSWR<RiskLiquidation[], boolean>(
    process.env.REACT_APP_API_LIQUIDATION,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000,
    },
  )

  const [optionsSymbol, setOptionsSymbol] = useState<string[]>([])
  const [optionsAccount, setOptionsAccount] = useState<string[]>([])

  const [leftFilters, setLeftFilters] = useState({
    symbol: '',
    account: ''
  })
  const [rightFilters, setRightFilters] = useState({
    symbol: '',
    account: ''
  })

  const [riskItems, setRiskItems] = useState<{
    left: RiskLiquidation[]
    right: RiskLiquidation[]
  }>({
    left: [],
    right: []
  })

  const filterItems = (data: RiskLiquidation, filters: { symbol: string, account: string }): boolean => {
    const symbolMatch = data.symbol_name.toLowerCase().includes(filters.symbol.toLowerCase())
    const accountMatch = data.owner.toLowerCase().includes(filters.account.toLowerCase())
    return symbolMatch && accountMatch
  }

  useEffect(() => {
    let dataToParse: RiskLiquidation[]
    if (error === true || data === undefined) {
      dataToParse = []
    } else {
      dataToParse = data
    }

    const processPanelData = (
      panelData: RiskLiquidation[],
      filters: { symbol: string, account: string },
      isRightPanel: boolean
    ): RiskLiquidation[] => {
      return panelData
        .filter((item) => filterItems(item, filters))
        .map(item => ({
          ...item,
          show_ratio: isRightPanel
        }))
        .sort((a, b) => {
          const keyA = typeof a.show_ratio === 'boolean' && a.show_ratio ? a.margin_ratio : a.portfolio_margin
          const keyB = typeof b.show_ratio === 'boolean' && b.show_ratio ? b.margin_ratio : b.portfolio_margin
          const isAValid = isValidNumberString(keyA)
          const isBValid = isValidNumberString(keyB)

          const absA = Math.abs(Number(keyA))
          const absB = Math.abs(Number(keyB))

          return (isAValid && isBValid)
            ? (absA > absB)
                ? 1
                : (absA === absB)
                    ? 0
                    : -1
            : (isAValid)
                ? -1
                : 1
        })
    }

    const leftRiskItems = processPanelData(dataToParse, leftFilters, false)
    const rightRiskItems = processPanelData(dataToParse, rightFilters, true)

    setRiskItems({
      left: leftRiskItems,
      right: rightRiskItems
    })

    setOptionsSymbol(
      Array.from(new Set(dataToParse.map(item => item.symbol_name))).sort()
    )
    setOptionsAccount(
      Array.from(new Set(dataToParse.map(item => item.owner))).sort()
    )
  }, [data, error, leftFilters, rightFilters])

  const { data: dataEquityRatio, error: errorEquityRatio } = useSWR<Record<string, string>, boolean>(
    process.env.REACT_APP_API_EQUITY_RATIO,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000,
    },
  )

  const [equityRatio, setEquityRatio] = useState<Record<string, string>>({})

  useEffect(() => {
    let equityRatio
    if (errorEquityRatio === true || dataEquityRatio === undefined) {
      equityRatio = {}
    } else {
      equityRatio = dataEquityRatio
    }
    setEquityRatio(equityRatio)
  }, [dataEquityRatio, errorEquityRatio])

  return (
    <Stack gap={4} sx={{ padding: 2 }}>
      <RiskSummary equityRatio={equityRatio} />

      <Stack direction="row" gap={4}>
        <RiskCardPanel
          side="left"
          showRatio={false}
          filterSymbol={leftFilters.symbol}
          setFilterSymbol={(value) => { setLeftFilters({ ...leftFilters, symbol: value }) }}
          filterAccount={leftFilters.account}
          setFilterAccount={(value) => { setLeftFilters({ ...leftFilters, account: value }) }}
          riskItems={riskItems.left}
          optionsSymbol={optionsSymbol}
          optionsAccount={optionsAccount}
        />

        <RiskCardPanel
          side="right"
          showRatio={true}
          filterSymbol={rightFilters.symbol}
          setFilterSymbol={(value) => { setRightFilters({ ...rightFilters, symbol: value }) }}
          filterAccount={rightFilters.account}
          setFilterAccount={(value) => { setRightFilters({ ...rightFilters, account: value }) }}
          riskItems={riskItems.right}
          optionsSymbol={optionsSymbol}
          optionsAccount={optionsAccount}
        />
      </Stack>
    </Stack>
  )
}

export default RiskCardList
